import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {EyeClosedIcon, EyeIcon, HeartFillIcon, HeartIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Link, Token} from '@primer/react'
import type {Column} from '@primer/react/experimental'
import {PrivacyLevel, type SponsorshipData} from './your-sponsorships-types'
import type {SponsorshipUpdate} from './update-sponsorship'

import styles from './Columns.module.css'
import {clsx} from 'clsx'

export const SponsorableColumn: Column<SponsorshipData> = {
  header: 'Name',
  field: 'sponsorableLogin',
  width: 'grow',
  renderCell: (row: SponsorshipData) => {
    return (
      <>
        <Link href={`/sponsors/${row.sponsorableLogin}`}>
          <div className={styles.Box}>
            <GitHubAvatar src={row.sponsorableAvatarUrl} square={row.sponsorableIsOrg} size={26} />
            <span className={styles.Text}>{`@${row.sponsorableLogin}`}</span>
          </div>
        </Link>
        {row.pendingChange && <Token text={row.pendingChange} className={styles.Token} />}
      </>
    )
  },
}

export const VisibilityColumn: Column<SponsorshipData> = {
  header: 'Visibility',
  field: 'privacyLevel',
  width: 'auto',
  renderCell: row => {
    return (
      <div className={styles.Box_1}>
        {row.privacyLevel === PrivacyLevel.PUBLIC ? (
          <EyeIcon className={clsx('color-fg-muted', styles.Octicon)} />
        ) : (
          <EyeClosedIcon className={clsx('color-fg-muted', styles.Octicon)} />
        )}
        {row.privacyLevel}
      </div>
    )
  },
}

export const StartDateColumn: Column<SponsorshipData> = {
  header: 'Start date',
  field: 'startDate',
  width: 'auto',
}

export const AmountColumn: Column<SponsorshipData> = {
  header: 'Amount',
  field: 'amount',
  width: 'auto',
}

export const ManageButtonColumn = (
  sponsorLogin: string,
  updateSponsorship: (sponsorshipUpate: SponsorshipUpdate) => void,
): Column<SponsorshipData> => {
  return {
    header: () => <span className="sr-only">Actions</span>,
    field: 'id',
    width: 'auto',
    renderCell: (row: SponsorshipData) => {
      // If the user has cancelled a sponsorship and has a pendingChange, the only way to undo that change is via the manageLink.
      // So manageLink will send the user to the main sponsorship management page if there's a pendingChange or if the sponsorship is not active.
      // Otherwise, manageLink will send the user to the specific tier management page
      let manageLink = `/sponsors/${row.sponsorableLogin}?sponsor=${sponsorLogin}`
      if (!row.pendingChange && row.active) {
        manageLink = `/sponsors/${row.sponsorableLogin}/sponsorships?sponsor=${sponsorLogin}&tier_id=${row.subscribableId}`
      }
      const oppositePrivacyLevel = row.privacyLevel === PrivacyLevel.PUBLIC ? PrivacyLevel.PRIVATE : PrivacyLevel.PUBLIC
      const subscribed = row.subscribedToNewsletterUpdates === undefined ? false : row.subscribedToNewsletterUpdates

      return (
        <ActionMenu>
          <ActionMenu.Button size="small" {...testIdProps('manage-button')}>
            Manage
          </ActionMenu.Button>
          <ActionMenu.Overlay width="medium">
            <ActionList>
              {row.active ? (
                <>
                  <ActionList.LinkItem
                    href={row.patreonLink || manageLink}
                    {...testIdProps('manage-sponsorship-button')}
                  >
                    Manage sponsorship
                  </ActionList.LinkItem>
                  <ActionList.Divider />
                  <ActionList.Item
                    onSelect={() => {
                      updateSponsorship({
                        sponsorableLogin: row.sponsorableLogin,
                        privacyLevel: oppositePrivacyLevel,
                        subscribedToNewsletterUpdates: subscribed,
                      })
                    }}
                    {...testIdProps('privacy-button')}
                  >
                    {row.privacyLevel === PrivacyLevel.PUBLIC ? 'Make Private' : 'Make Public'}
                  </ActionList.Item>
                  <ActionList.Item
                    onSelect={() => {
                      updateSponsorship({
                        sponsorableLogin: row.sponsorableLogin,
                        privacyLevel: row.privacyLevel || PrivacyLevel.PRIVATE,
                        subscribedToNewsletterUpdates: !row.subscribedToNewsletterUpdates,
                      })
                    }}
                    {...testIdProps('subscribe-button')}
                  >
                    {row.subscribedToNewsletterUpdates ? 'Unsubscribe from emails' : 'Subscribe to email updates'}
                  </ActionList.Item>
                </>
              ) : (
                <>
                  <ActionList.LinkItem
                    href={row.patreonLink || manageLink}
                    {...testIdProps('manage-past-sponsorship-button')}
                  >
                    Re-sponsor
                  </ActionList.LinkItem>
                  <ActionList.Divider />
                  <ActionList.Item
                    onSelect={() => {
                      updateSponsorship({
                        sponsorableLogin: row.sponsorableLogin,
                        privacyLevel: oppositePrivacyLevel,
                      })
                    }}
                    {...testIdProps('privacy-button-past')}
                  >
                    {row.privacyLevel === PrivacyLevel.PUBLIC ? 'Make Private' : 'Make Public'}
                  </ActionList.Item>
                </>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )
    },
  }
}

export const SponsorButtonColumn: Column<SponsorshipData> = {
  header: () => <span className="sr-only">Actions</span>,
  field: 'id',
  width: 'auto',
  renderCell: (row: SponsorshipData) => {
    const link = `/sponsors/${row.sponsorableLogin}`
    return (
      <Button
        as="a"
        size="small"
        href={link}
        leadingVisual={row.viewerIsSponsor ? HeartFillIcon : HeartIcon}
        className={styles.Button}
        {...testIdProps('sponsor-button')}
      >
        {row.viewerIsSponsor ? 'Sponsoring' : 'Sponsor'}
      </Button>
    )
  },
}
